import {Trans} from '@lingui/macro'
import {useFormik} from 'formik'
import React, {useEffect} from 'react'
import styled, {css} from 'styled-components'

import {theme, tokens, VisuallyHidden} from '@pleo-io/telescope'

import {Keys} from '@product-web/web-platform/keyboard'

const INPUT_LENGTH = 4

type FormValues = {
    passcode: string
}

export function PasscodeInput({
    onSubmit,
    hasError,
    alignLeft = false,
}: {
    onSubmit: (value: string) => void
    hasError?: boolean
    alignLeft?: boolean
}) {
    const {element} = usePasscodeInput({onSubmit, hasError, alignLeft})

    return <>{element}</>
}

export function usePasscodeInput({
    onSubmit,
    hasError,
    alignLeft = false,
}: {
    onSubmit: (value: string) => void
    hasError?: boolean
    alignLeft?: boolean
}) {
    const formik = useFormik<FormValues>({
        initialValues: {
            passcode: '',
        },
        initialTouched: {passcode: true},
        validateOnBlur: true,
        onSubmit: ({passcode}, {setSubmitting}) => {
            if (passcode.length !== INPUT_LENGTH || !passcode.match(/^\d{0,4}$/)) {
                return
            }
            onSubmit(passcode)
            setSubmitting(false)
        },
    })

    const [isFocused, setIsFocused] = React.useState(false)
    const inputRef = React.useRef<HTMLInputElement>(null)

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === Keys.ARROW_UP || e.key === Keys.ARROW_DOWN) {
            e.preventDefault()
        }
    }

    const focus = () => {
        if (inputRef.current) {
            inputRef.current.focus()
        }
    }

    useEffect(() => {
        focus()
    }, [])

    const element = (
        <Wrapper>
            <Form onSubmit={formik.handleSubmit} $alignLeft={alignLeft}>
                <PinField
                    ref={inputRef}
                    $hasError={Boolean(hasError)}
                    disabled={hasError}
                    data-testid="passcode-input"
                    name="passcode"
                    type="password"
                    pattern="[0-9]*"
                    inputMode="numeric"
                    autoComplete="off"
                    id="passcode-input"
                    onKeyDown={handleKeyDown}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value.trim()
                        if (!value.match(/^\d{0,4}$/) || value.length > INPUT_LENGTH) {
                            return
                        }

                        formik.handleChange(e)
                        formik.setFieldValue('passcode', value, true)

                        if (value.length === INPUT_LENGTH) {
                            formik.handleSubmit()
                        }
                    }}
                    value={formik.values.passcode}
                    onBlur={() => setIsFocused(false)}
                    onFocus={() => setIsFocused(true)}
                />
                <VisuallyHidden>
                    <label htmlFor="passcode-input">
                        <Trans>Passcode</Trans>
                    </label>
                </VisuallyHidden>
                <InputElements
                    hasError={hasError}
                    code={formik.values.passcode}
                    isFocused={isFocused}
                />
            </Form>
        </Wrapper>
    )

    return {
        element,
        reset: formik.resetForm,
        focus,
    }
}

function InputElements({
    code,
    isFocused,
    hasError,
}: {
    code: string
    isFocused?: boolean
    hasError?: boolean
}) {
    return (
        <>
            {new Array(INPUT_LENGTH).fill({}).map((_, index: number) => {
                const value = code.substr(index, 1)
                return (
                    <InputPlaceholder
                        $hasError={hasError}
                        aria-hidden="true" // We hide this for assistive technology since it's purely stylistic
                        key={`code-input-${index}`}
                        $hasValue={Boolean(value)}
                        $isFocused={code.length === index && isFocused}
                    />
                )
            })}
        </>
    )
}
const PinField = styled.input<{$hasError: boolean}>`
    position: absolute;
    top: 0;
    left: 0;
    z-index: ${tokens.zIndexOverlay};
    width: 100%;
    height: 60px;
    box-sizing: border-box;
    padding: 0 ${tokens.spacing36};
    font-family: 'Courier New', monospace, serif;
    font-size: 20px;
    color: ${theme.colors.shade600};
    letter-spacing: 63px; /* This is a hardcoded value to create spacing for the input prompt */
    background: transparent;
    border: none;
    outline: none;
    -webkit-text-fill-color: transparent;
    appearance: textfield;

    ${({$hasError, disabled}) =>
        $hasError &&
        disabled &&
        css`
            &:disabled {
                z-index: -1;
            }
        `}

    &&::-ms-clear {
        display: none;
    }

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
        margin: 0;
        appearance: none;
    }
`

const focusedStyles = css`
    border-color: ${theme.colors.shade900};
`

const errorStyles = css`
    &::before {
        background-color: ${tokens.red700};
    }
`

const valueStyles = css`
    &::before {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 20px;
        height: 20px;
        content: '';
        background-color: ${tokens.shade800};
        border-radius: ${tokens.circle};
        transform: translate(-50%, -50%);
    }
`

const InputPlaceholder = styled.div<{
    $isFocused?: boolean
    $hasValue?: boolean
    $hasError?: boolean
}>`
    position: relative;
    z-index: ${tokens.zIndexSurface};
    display: flex;
    min-width: 60px;
    max-width: 60px;
    height: 60px;
    box-sizing: border-box;
    background: ${theme.colors.shade300};
    border: 4px solid ${tokens.shade300};
    border-radius: ${tokens.circle};
    transition: ${tokens.fastIn};
    transition-property: border;
    flex: 0 0 60px;

    ${({$hasValue}) => $hasValue && valueStyles}
    ${({$isFocused}) => $isFocused && focusedStyles}
    ${({$hasError}) => $hasError && errorStyles}
`

const Form = styled.form<{
    $alignLeft?: boolean
}>`
    position: relative;
    display: flex;
    width: 311px;
    margin: auto;
    white-space: nowrap;
    flex-direction: row;
    justify-content: space-around;

    ${({$alignLeft}) =>
        $alignLeft &&
        css`
            justify-content: flex-start;
            gap: ${tokens.spacing16};
            margin: 0;
        `}

    /* Needed to override browser native password managers - https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion#the_autocomplete_attribute_and_login_fields */
    input:-webkit-autofill,
    input:focus:-webkit-autofill {
        background-clip: text;

        &::selection {
            color: transparent;
            background: none;
        }
    }
`

const Wrapper = styled.div`
    width: 100%;
`
